import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Autocomplete,
  Popover,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs from 'dayjs';
import { DEPARTMENT_CHOICES, STATUS_CHOICES } from '../../utils/employeeConstants';
import DateRangeFilter from '../DateRangeFilter';

const PRESET_LABELS = {
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  thisYear: 'This Year',
  last3Months: 'Last 3 Months',
  last6Months: 'Last 6 Months',
  lastYear: 'Last Year',
  custom: 'Custom Range',
};

const formatDate = (value) => (value ? dayjs(value).format('MMM DD, YYYY') : '');

const getDateRangeSummary = (dateRange) => {
  if (!dateRange?.preset || dateRange.preset === 'All') return null;
  if (dateRange.preset === 'custom') {
    if (dateRange.startDate && dateRange.endDate) {
      return `${formatDate(dateRange.startDate)} – ${formatDate(dateRange.endDate)}`;
    }
    if (dateRange.startDate) return `From ${formatDate(dateRange.startDate)}`;
    if (dateRange.endDate) return `Until ${formatDate(dateRange.endDate)}`;
    return 'Custom Range';
  }
  return PRESET_LABELS[dateRange.preset] || null;
};

const EmployeeFilters = ({
  searchTerm,
  departmentFilter,
  statusFilter,
  filterOptions,
  dateRange,
  onSearchChange,
  onFilterChange,
  onDateRangeChange,
  onClearFilters,
}) => {
  const [dateAnchor, setDateAnchor] = useState(null);

  const departmentOptions = filterOptions?.departments || DEPARTMENT_CHOICES;
  const statusOptions = filterOptions?.status_choices || STATUS_CHOICES;

  const handleOpenDate = (event) => setDateAnchor(event.currentTarget);
  const handleCloseDate = () => setDateAnchor(null);

  const handleDateRangeChange = (newRange) => {
    onDateRangeChange(newRange);
    if (newRange?.preset && newRange.preset !== 'custom') {
      handleCloseDate();
    }
  };

  const handleClearDate = () => {
    onDateRangeChange({ startDate: null, endDate: null, preset: 'All' });
    handleCloseDate();
  };

  const dateSummary = getDateRangeSummary(dateRange);
  const isDateActive = Boolean(dateSummary);
  const dateButtonLabel = dateSummary || 'Joining Date';

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search employees..."
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              value={searchTerm}
              onChange={onSearchChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <Autocomplete
              size="small"
              value={
                DEPARTMENT_CHOICES.find((dept) => dept.value === departmentFilter) || {
                  value: 'All',
                  label: 'All Departments',
                }
              }
              onChange={(event, newValue) => {
                onFilterChange('department', newValue?.value || 'All');
              }}
              options={[{ value: 'All', label: 'All Departments' }, ...departmentOptions]}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => <TextField {...params} label="Department" />}
              clearOnEscape
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <Autocomplete
              size="small"
              value={{
                value: statusFilter,
                label: statusFilter === 'All' ? 'All Status' : statusFilter === 'active' ? 'Active' : 'Inactive',
              }}
              onChange={(event, newValue) => {
                onFilterChange('status', newValue?.value || 'All');
              }}
              options={statusOptions}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => <TextField {...params} label="Status" />}
              clearOnEscape
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleOpenDate}
              startIcon={<CalendarTodayIcon />}
              sx={{
                height: '40px',
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: isDateActive ? 'text.primary' : 'text.secondary',
                borderColor: isDateActive ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
                fontWeight: isDateActive ? 600 : 400,
                '&:hover': {
                  borderColor: isDateActive ? 'primary.dark' : 'text.primary',
                  backgroundColor: 'transparent',
                },
                '& .MuiButton-startIcon': {
                  color: isDateActive ? 'primary.main' : 'text.secondary',
                },
                overflow: 'hidden',
              }}
            >
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                {dateButtonLabel}
              </Box>
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button variant="outlined" onClick={onClearFilters} size="small" fullWidth sx={{ height: '40px' }}>
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </CardContent>

      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={handleCloseDate}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 1.5,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              width: { xs: 'calc(100vw - 32px)', sm: 520 },
              maxWidth: 'calc(100vw - 32px)',
            },
          },
        }}
      >
        <Box sx={{ p: 2.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Joining Date
            </Typography>
            {isDateActive && (
              <Button size="small" onClick={handleClearDate} sx={{ textTransform: 'none', minWidth: 0, px: 1 }}>
                Clear
              </Button>
            )}
          </Stack>

          <DateRangeFilter
            onDateRangeChange={handleDateRangeChange}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            selectedPreset={dateRange.preset}
            label="Joining Date"
            sx={{
              height: 'auto',
              flexWrap: 'wrap',
              gap: 2,
              '& > :not(style) ~ :not(style)': { ml: 0 },
              '& > .MuiBox-root:first-of-type': {
                width: '100%',
                flex: '1 1 100%',
              },
              '& .MuiTextField-root': {
                width: { xs: '100%', sm: 'calc(50% - 8px)' },
                minWidth: 0,
              },
            }}
          />
        </Box>
      </Popover>
    </Card>
  );
};

EmployeeFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  departmentFilter: PropTypes.string.isRequired,
  statusFilter: PropTypes.string.isRequired,
  filterOptions: PropTypes.shape({
    departments: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ),
    status_choices: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ),
  }),
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    preset: PropTypes.string,
  }).isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

EmployeeFilters.defaultProps = {
  filterOptions: {},
};

export default EmployeeFilters;
